/**
 * Utility function for retrying API calls with exponential backoff
 * Specifically designed for handling timing issues with file uploads
 */

/**
 * Retry an async operation with exponential backoff
 * @param {Function} operation - Async function to retry
 * @param {Object} options - Configuration options
 * @param {number} options.maxRetries - Maximum number of retry attempts (default: 5)
 * @param {number} options.initialDelay - Initial delay in milliseconds (default: 1000)
 * @param {number} options.maxDelay - Maximum delay in milliseconds (default: 10000)
 * @param {number} options.backoffMultiplier - Multiplier for exponential backoff (default: 2)
 * @param {Function} options.shouldRetry - Function to determine if error should trigger retry
 * @param {string} options.operationName - Name for logging purposes
 * @returns {Promise} - Result of the successful operation
 */
export async function retryWithBackoff(operation, options = {}) {
  const {
    maxRetries = 5,
    initialDelay = 1000,
    maxDelay = 10000,
    backoffMultiplier = 2,
    shouldRetry = defaultShouldRetry,
    operationName = 'operation'
  } = options;

  let lastError;
  let delay = initialDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[Retry] Attempting ${operationName} (attempt ${attempt + 1}/${maxRetries + 1})`);
      
      // If this is not the first attempt, wait before retrying
      if (attempt > 0) {
        console.log(`[Retry] Waiting ${delay}ms before retry attempt ${attempt + 1}`);
        await sleep(delay);
      }

      const result = await operation();
      
      if (attempt > 0) {
        console.log(`[Retry] ${operationName} succeeded on attempt ${attempt + 1}`);
      }
      
      return result;
    } catch (error) {
      lastError = error;
      
      console.log(`[Retry] ${operationName} failed on attempt ${attempt + 1}:`, error.message);
      
      // Check if this is the last attempt or if we shouldn't retry this error
      if (attempt === maxRetries || !shouldRetry(error)) {
        if (!shouldRetry(error)) {
          console.log(`[Retry] ${operationName} failed with non-retryable error:`, error.message);
        } else {
          console.log(`[Retry] ${operationName} failed after ${maxRetries + 1} attempts`);
        }
        throw lastError;
      }
      
      // Calculate next delay with exponential backoff and jitter
      const jitter = Math.random() * 0.1 * delay; // Add up to 10% jitter
      delay = Math.min(delay * backoffMultiplier + jitter, maxDelay);
    }
  }
}

/**
 * Default function to determine if an error should trigger a retry
 * @param {Error} error - The error that occurred
 * @returns {boolean} - Whether to retry the operation
 */
function defaultShouldRetry(error) {
  // Retry on network errors
  if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
    return true;
  }
  
  // Retry on timeout errors
  if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
    return true;
  }
  
  // Retry on server errors (5xx)
  if (error.response && error.response.status >= 500) {
    return true;
  }
  
  // Retry on specific "file not found" errors that indicate timing issues
  if (error.response && error.response.status === 404) {
    const errorMessage = error.response.data?.error || error.response.data?.message || '';
    if (errorMessage.includes('Uploaded file not found') || 
        errorMessage.includes('Video file not found') ||
        errorMessage.includes('file not found')) {
      return true;
    }
  }
  
  // Don't retry on client errors (4xx) except 404 file not found
  if (error.response && error.response.status >= 400 && error.response.status < 500) {
    return false;
  }
  
  // Retry on unknown errors
  return true;
}

/**
 * Utility function to sleep for a specified number of milliseconds
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise} - Promise that resolves after the delay
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Specific retry function for upload project API calls
 * @param {Function} uploadPaperOperation - Function that calls the upload project API
 * @param {string} resourceId - The resource ID being uploaded
 * @returns {Promise} - Result of the successful upload project call
 */
export async function retryUploadPaper(uploadPaperOperation, resourceId) {
  return retryWithBackoff(uploadPaperOperation, {
    maxRetries: 5,
    initialDelay: 1000,
    maxDelay: 8000,
    operationName: `upload_paper for resource ${resourceId}`,
    shouldRetry: (error) => {
      // Custom retry logic for upload project
      // Handle body-level 404 (backend returns HTTP 200 with status:404 in body)
      if (error.responseStatus === 404 || (error.response && error.response.status === 404)) {
        const errorMessage = error.message || error.response?.data?.error || '';
        return errorMessage.includes('Uploaded file not found');
      }
      // Don't retry auth failures or other body-level errors
      if (error.responseStatus) {
        return false;
      }
      return defaultShouldRetry(error);
    }
  });
}

/**
 * Specific retry function for complete recording API calls
 * @param {Function} completeRecordingOperation - Function that calls the complete recording API
 * @param {string} videoFileUid - The video file UID being processed
 * @returns {Promise} - Result of the successful complete recording call
 */
export async function retryCompleteRecording(completeRecordingOperation, videoFileUid) {
  return retryWithBackoff(completeRecordingOperation, {
    maxRetries: 5,
    initialDelay: 1000,
    maxDelay: 8000,
    operationName: `complete_recording for video ${videoFileUid}`,
    shouldRetry: (error) => {
      // Custom retry logic for complete recording
      if (error.response && error.response.status === 404) {
        const errorMessage = error.response.data?.message || '';
        return errorMessage.includes('Video file not found');
      }
      return defaultShouldRetry(error);
    }
  });
}
